import { useFormikContext } from 'formik';
import type { ChangeEvent, InputHTMLAttributes, ReactNode } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Color } from '@tmh-website/shared/colors';
import { type FormElementCommonProps, changeNumberValue } from '@tmh-website/ui/FormElements';
import Input, { InputProps } from '@tmh-website/ui/Input';

import * as Styled from './NumberInput.styles';

export interface NumberInputProps
  extends FormElementCommonProps,
    Partial<Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'name'>> {
  helpText?: string | ReactNode;
  min?: number;
  max?: number;
  step?: number;
  smallLabel?: boolean;
  format?: InputProps['format'];
}

const NumberInput = ({
  themeColor = Color.Neutral_0_White,
  name,
  disabled = false,
  min,
  max,
  step = 1,
  format,
  value,
  ...props
}: NumberInputProps) => {
  const { t } = useTranslation();
  const { values, setFieldTouched, setFieldValue } = useFormikContext<any>();
  const [lastChangeEvent, setLastChangeEvent] = useState<ChangeEvent<HTMLInputElement> | undefined>();

  /**
   * Prevent keyboard input from breaking min/max
   */
  function updateValueFromLastChange() {
    if (!lastChangeEvent || !props.onChange) {
      return;
    }

    let newValue = parseInt(lastChangeEvent.target.value);

    if (max !== undefined && newValue > max) {
      newValue = max;
    } else if (min !== undefined && newValue < min) {
      newValue = min;
    }

    lastChangeEvent.target.value = newValue.toString();

    props.onChange(lastChangeEvent);
    setLastChangeEvent(undefined);
  }

  return (
    <Styled.Wrapper>
      <Input
        themeColor={themeColor}
        disabled={disabled}
        name={name}
        type='number'
        min={min}
        max={max}
        step={step}
        format={format}
        {...props}
        value={lastChangeEvent ? lastChangeEvent.target.value : value}
        onChange={setLastChangeEvent}
        onKeyUp={e => {
          // Update value when pressing enter in the field
          if (e.key === 'Enter' && lastChangeEvent) {
            e.preventDefault();
            updateValueFromLastChange();
          }
          if (props.onKeyUp) {
            props.onKeyUp(e);
          }
        }}
        onBlur={() => {
          if (lastChangeEvent) {
            updateValueFromLastChange();
          }
        }}
        elementRight={
          <div>
            <Styled.ChangeValueButton
              type='button'
              $disabled={disabled || value === max}
              $themeColor={themeColor}
              onClick={() => changeNumberValue(1, step, values, name, setFieldValue, setFieldTouched, min, max)}
              aria-label={t('global:numberInput.increase') as string}>
              <svg width='8' height='8' fill='currentColor'>
                <path d='M4 0a1 1 0 0 0-1 1v2H1a1 1 0 0 0 0 2h2v2a1 1 0 0 0 2 0V5h2a1 1 0 1 0 0-2H5V1a1 1 0 0 0-1-1z' />
              </svg>
            </Styled.ChangeValueButton>
            <Styled.ChangeValueButton
              type='button'
              $disabled={disabled || value === min}
              $themeColor={themeColor}
              onClick={() => changeNumberValue(-1, step, values, name, setFieldValue, setFieldTouched, min, max)}
              aria-label={t('global:numberInput.decrease') as string}>
              <svg width='8' height='8' fill='currentColor'>
                <rect x='.5' y='3' width='7' height='2' rx='1' />
              </svg>
            </Styled.ChangeValueButton>
          </div>
        }
      />
    </Styled.Wrapper>
  );
};

export default NumberInput;
